import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    if (this.refs.cartBubbleCount) {
      this.refs.cartBubbleCount.textContent = value <= 99 ? (value > 0 ? String(value) : '') : '99+';
    }
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.addEventListener('cart:update', this.onGenericCartUpdate);
    document.addEventListener('cart:updated', this.onGenericCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.removeEventListener('cart:update', this.onGenericCartUpdate);
    document.removeEventListener('cart:updated', this.onGenericCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles generic cart events from custom cart drawer or theme forms.
   * @param {CustomEvent} event
   */
  onGenericCartUpdate = (event) => {
    const cart = event?.detail?.resource || event?.detail?.cart;
    const count = cart?.item_count ?? cart?.totalQuantity ?? event?.detail?.data?.itemCount ?? event?.detail?.itemCount;
    if (typeof count === 'number') {
      this.renderCartBubble(count);
    }
  };

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount = cart?.item_count ?? cart?.totalQuantity ?? detail?.itemCount ?? 0;

        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    if (!this.refs.cartBubble || !this.refs.cartBubbleCount) return;

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.toggleAttribute('data-maintain-ratio', itemCount <= 99);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    if (this.refs.cartBubbleText) {
      await onAnimationEnd(this.refs.cartBubbleText);
    }

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    if (sessionStorageCount !== null) {
      const visibleCount = this.refs.cartBubbleCount.textContent;

      try {
        const { value, timestamp } = JSON.parse(sessionStorageCount);

        // Check if the stored count matches what's visible
        if (value !== visibleCount && Date.now() - timestamp < 30000) {
          const count = parseInt(value, 10);

          if (count >= 0) {
            this.renderCartBubble(count, false);
          }
        }
      } catch (_) {
        // no-op
      }
    }

    // Always sync with live cart API to guarantee 100% correct count
    const rootUrl = window.Shopify?.routes?.root || '/';
    fetch(rootUrl + 'cart.js')
      .then((res) => res.json())
      .then((cart) => {
        if (cart && typeof cart.item_count === 'number') {
          this.renderCartBubble(cart.item_count, false);
        }
      })
      .catch(() => {});
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
